import { useState } from "react";
import { Link } from "react-router-dom";
import { Shirt, Check, Loader2, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { submitMerchInterest } from "../lib/api";
import SEO from "../components/SEO";

const MERCH_ITEMS = ["Hoodie", "T-shirt", "Cap", "Longsleeve", "Shotglas", "Sticker-pack"];

const Merchandise = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const toggle = (v) => {
    setPicks((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitMerchInterest({ email: email.trim(), name: name.trim() || undefined, interests: picks });
      setDone(true);
      toast.success("Je staat op de lijst!");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Aanmelden mislukt.";
      toast.error(typeof msg === "string" ? msg : "Aanmelden mislukt.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="merchandise-page" className="relative min-h-screen">
      <SEO
        title="Merch · Coming Soon"
        description="Hoodies, shirts, caps en shotglazen — gebouwd in dezelfde stijl als de shots. Meld je aan en je krijgt de drop als eerste in je inbox."
        path="/merchandise"
      />
      <section className="relative py-24 sm:py-32 border-b border-[var(--tha-border)]">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <div className="inline-flex items-center gap-3 mx-auto">
            <span className="h-px w-12 bg-[var(--tha-red)]" />
            <span className="text-xs font-mono-x tracking-[0.3em] text-[var(--tha-red)]">
              MERCH · IN DE MAAK
            </span>
            <span className="h-px w-12 bg-[var(--tha-red)]" />
          </div>
          <div className="mt-8 flex justify-center">
            <div className="inline-flex w-24 h-24 items-center justify-center border-2 border-[var(--tha-red)]">
              <Shirt size={48} className="text-[var(--tha-red)]" />
            </div>
          </div>
          <h1
            data-testid="merch-title"
            className="font-display text-6xl sm:text-8xl md:text-9xl mt-10 leading-[0.85]"
          >
            COMING
            <br />
            <span className="neon-red-text">SOON.</span>
          </h1>
          <p className="mt-8 text-[var(--tha-muted)] max-w-xl mx-auto text-base sm:text-lg leading-relaxed">
            De merchdrop is onderweg. Hoodies, shirts, caps en shotglazen —
            gebouwd in dezelfde stijl als de shots. Meld je aan en je krijgt 'm
            als eerste in je inbox.
          </p>

          {done ? (
            <div data-testid="merch-success" className="mt-14 border border-[var(--tha-red)] bg-[#1a0303] p-10 max-w-lg mx-auto">
              <div className="inline-flex w-14 h-14 items-center justify-center border-2 border-[var(--tha-red)] text-[var(--tha-red)]">
                <Check size={28} />
              </div>
              <div className="font-display text-3xl mt-5 leading-none">
                JIJ STAAT <span className="neon-red-text">EROP.</span>
              </div>
              <p className="mt-4 text-[var(--tha-muted)] text-sm">
                We tikken je aan zodra de eerste stukken online komen.
              </p>
              <Link
                to="/"
                className="mt-6 btn-ghost-red inline-flex items-center gap-2 px-6 py-3 font-display tracking-wider text-sm"
              >
                TERUG NAAR HOME <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="merch-form"
              className="mt-14 max-w-lg mx-auto space-y-6 text-left"
            >
              <label className="flex flex-col gap-1.5 p-4 border border-[var(--tha-border)] bg-[#050505] focus-within:border-white transition-colors">
                <span className="text-[10px] font-mono-x tracking-[0.25em] text-[var(--tha-muted)] inline-flex items-center gap-2">
                  <Mail size={11} /> E-MAIL <span className="text-[var(--tha-red)]">*</span>
                </span>
                <input
                  data-testid="merch-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                  placeholder="jouw@email.nl"
                />
              </label>

              <label className="flex flex-col gap-1.5 p-4 border border-[var(--tha-border)] bg-[#050505] focus-within:border-white transition-colors">
                <span className="text-[10px] font-mono-x tracking-[0.25em] text-[var(--tha-muted)]">
                  NAAM (OPTIONEEL)
                </span>
                <input
                  data-testid="merch-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-white placeholder-[var(--tha-muted)]"
                  placeholder="Jouw naam"
                />
              </label>

              <div>
                <div className="text-[10px] font-mono-x tracking-[0.25em] text-[var(--tha-muted)] mb-3 text-left">
                  WAAR HEB JE INTERESSE IN? (OPTIONEEL)
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {MERCH_ITEMS.map((m) => {
                    const active = picks.includes(m);
                    return (
                      <button
                        type="button"
                        key={m}
                        data-testid={`merch-interest-${m.toLowerCase()}`}
                        onClick={() => toggle(m)}
                        className={`text-left px-4 py-3 border text-xs font-mono-x tracking-wider transition-colors ${
                          active
                            ? "border-[var(--tha-red)] bg-[#1a0303] text-white"
                            : "border-[var(--tha-border)] bg-[#050505] text-[var(--tha-muted)] hover:text-white hover:border-white"
                        }`}
                      >
                        <span className="inline-flex items-center gap-2">
                          <span
                            className={`inline-block w-3 h-3 border ${
                              active
                                ? "bg-[var(--tha-red)] border-[var(--tha-red)]"
                                : "border-[var(--tha-muted)]"
                            }`}
                          />
                          {m}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <button
                data-testid="merch-submit"
                type="submit"
                disabled={loading}
                className="w-full py-4 font-display tracking-wider btn-red inline-flex items-center justify-center gap-3 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                {loading ? "AANMELDEN..." : "HOUD MIJ OP DE HOOGTE"}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Merchandise;
